/**
 * 🔮 KETO BUDDY TOUR - WORLD CLASS ONBOARDING
 * ============================================
 * Tour guiado pelo Keto Buddy Spirit
 * Explica todo o ecossistema em módulos
 * 
 * Suporta: PT / EN
 * Voice: Browser TTS + OpenAI TTS ready
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { 
  ChevronRight, ChevronLeft, X, Volume2, VolumeX,
  Home, Dumbbell, Timer, ScanFace, ChefHat, Brain,
  Trophy, Dna, Users, GraduationCap, Target, Sparkles,
  Play, Pause, SkipForward, CheckCircle, Rocket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

// Tour content - bilingual
const TOUR_MODULES = [
  {
    id: 'welcome',
    icon: Sparkles,
    color: '#a3e635',
    gradient: 'from-[#a3e635] to-emerald-500',
    title: {
      en: 'Welcome to Keto Lab',
      pt: 'Bem-vindo ao Keto Lab'
    },
    subtitle: {
      en: 'Your AI-Powered Transformation Journey',
      pt: 'A Tua Jornada de Transformação com IA'
    },
    content: {
      en: "Hello! I'm Spirit, your personal transformation guide. I'll be with you every step of the way. Let me show you everything I can do to help you achieve your goals.",
      pt: "Olá! Sou o Spirit, o teu guia pessoal de transformação. Vou estar contigo em cada passo. Deixa-me mostrar-te tudo o que posso fazer para te ajudar a atingir os teus objetivos.",
      es: "¡Hola! Soy Spirit, tu guía personal de transformación. Estaré contigo en cada paso. Déjame mostrarte todo lo que puedo hacer para ayudarte a alcanzar tus metas."
    },
    tips: {
      en: ["I learn from you", "I adapt to your needs", "I'm always here to help"],
      pt: ["Aprendo contigo", "Adapto-me às tuas necessidades", "Estou sempre aqui para ajudar"]
    }
  },
  {
    id: 'hub',
    icon: Home,
    color: '#a3e635',
    gradient: 'from-[#a3e635] to-emerald-500',
    path: '/hub',
    title: {
      en: 'The Hub - Your Command Center',
      pt: 'O Hub - O Teu Centro de Comando'
    },
    subtitle: {
      en: 'Everything starts here',
      pt: 'Tudo começa aqui'
    },
    content: {
      en: "This is your Hub - the heart of Keto Lab. From here you can access all features, see your progress at a glance, and I'll give you personalized suggestions based on your journey.",
      pt: "Este é o teu Hub - o coração do Keto Lab. A partir daqui podes aceder a todas as funcionalidades, ver o teu progresso num relance, e eu dou-te sugestões personalizadas baseadas na tua jornada."
    },
    tips: {
      en: ["Quick access to all features", "Daily stats overview", "Personalized recommendations"],
      pt: ["Acesso rápido a tudo", "Resumo diário das estatísticas", "Recomendações personalizadas"]
    }
  },
  {
    id: 'fasting',
    icon: Timer,
    color: '#a855f7',
    gradient: 'from-purple-400 to-purple-600',
    path: '/fasting',
    title: {
      en: 'Intermittent Fasting',
      pt: 'Jejum Intermitente'
    },
    subtitle: {
      en: 'Master your metabolic flexibility',
      pt: 'Domina a tua flexibilidade metabólica'
    },
    content: {
      en: "Fasting is one of the most powerful tools for transformation. I track your fasting windows, show you what's happening in your body at each phase, and help you choose the right protocol for your lifestyle.",
      pt: "O jejum é uma das ferramentas mais poderosas para transformação. Eu acompanho as tuas janelas de jejum, mostro-te o que está a acontecer no teu corpo em cada fase, e ajudo-te a escolher o protocolo certo para o teu estilo de vida."
    },
    tips: {
      en: ["Multiple protocols (16:8, 18:6, 20:4, OMAD)", "Real-time body phase tracking", "Science-backed education"],
      pt: ["Múltiplos protocolos (16:8, 18:6, 20:4, OMAD)", "Acompanhamento de fases em tempo real", "Educação baseada em ciência"]
    }
  },
  {
    id: 'training',
    icon: Dumbbell,
    color: '#f97316',
    gradient: 'from-red-400 to-orange-500',
    path: '/training',
    title: {
      en: 'Training AI',
      pt: 'Training AI'
    },
    subtitle: {
      en: 'Your autonomous personal trainer',
      pt: 'O teu personal trainer autónomo'
    },
    content: {
      en: "I generate unique workouts for every session - no templates, no repetition. I adapt to your fitness level, available equipment, and goals. Turn on your camera and I'll analyze your form in real-time, correcting you as you train.",
      pt: "Eu gero treinos únicos para cada sessão - sem templates, sem repetição. Adapto-me ao teu nível de fitness, equipamento disponível e objetivos. Liga a câmera e eu analiso a tua forma em tempo real, corrigindo-te enquanto treinas."
    },
    tips: {
      en: ["Dynamic AI-generated workouts", "Real-time form analysis", "Voice coaching", "Post-session reports"],
      pt: ["Treinos gerados por IA dinâmicos", "Análise de forma em tempo real", "Coaching por voz", "Relatórios pós-treino"]
    }
  },
  {
    id: 'ketoscan',
    icon: ScanFace,
    color: '#06b6d4',
    gradient: 'from-cyan-400 to-blue-500',
    path: '/ketoscan',
    title: {
      en: 'KetoScan',
      pt: 'KetoScan'
    },
    subtitle: {
      en: 'AI-powered food analysis',
      pt: 'Análise de alimentos com IA'
    },
    content: {
      en: "Point your camera at any food and I'll instantly tell you if it's keto-friendly. I analyze nutritional content, give you a Keto Score, and suggest alternatives if needed. No more guessing!",
      pt: "Aponta a câmera para qualquer alimento e eu digo-te instantaneamente se é keto-friendly. Analiso o conteúdo nutricional, dou-te um Keto Score, e sugiro alternativas se necessário. Sem mais adivinhações!"
    },
    tips: {
      en: ["Instant food recognition", "Keto Score 0-100", "Macro breakdown", "Healthy alternatives"],
      pt: ["Reconhecimento instantâneo", "Keto Score 0-100", "Análise de macros", "Alternativas saudáveis"]
    }
  },
  {
    id: 'nutrition',
    icon: ChefHat,
    color: '#a3e635',
    gradient: 'from-[#a3e635] to-[#65a30d]',
    path: '/meals',
    title: {
      en: 'AI Nutrition',
      pt: 'Nutrição IA'
    },
    subtitle: {
      en: 'Your personal keto chef',
      pt: 'O teu chef keto pessoal'
    },
    content: {
      en: "I create personalized meal plans, generate shopping lists, and even suggest recipes based on ingredients you have at home. Tell me what you're craving and I'll make it keto-friendly!",
      pt: "Eu crio planos alimentares personalizados, gero listas de compras, e até sugiro receitas baseadas nos ingredientes que tens em casa. Diz-me o que te apetece e eu torno-o keto-friendly!"
    },
    tips: {
      en: ["Weekly meal planning", "Smart shopping lists", "AI recipe generation", "Macro tracking"],
      pt: ["Planeamento semanal", "Listas de compras inteligentes", "Geração de receitas IA", "Tracking de macros"]
    }
  },
  {
    id: 'bodytwin',
    icon: Dna,
    color: '#ec4899',
    gradient: 'from-pink-400 to-rose-500',
    path: '/body-twin-hub',
    title: {
      en: 'Body Twin',
      pt: 'Body Twin'
    },
    subtitle: {
      en: 'See your transformation come alive',
      pt: 'Vê a tua transformação ganhar vida'
    },
    content: {
      en: "Upload a photo and I'll create your digital twin showing BEFORE and AFTER. But that's not all - I generate a MORPHING VIDEO showing your body transforming in real-time! For Premium users, track your progress week by week in the Chronology with AI-generated comparisons.",
      pt: "Carrega uma foto e eu crio o teu gémeo digital mostrando o ANTES e DEPOIS. Mas não é tudo - gero um VÍDEO COM MORPHING mostrando o teu corpo a transformar-se em tempo real! Para utilizadores Premium, acompanha o teu progresso semana a semana na Cronologia com comparações geradas por IA."
    },
    tips: {
      en: ["AI body visualization with face swap", "Morphing video transformation", "Premium: 8-week Chronology", "Scientific body fat analysis"],
      pt: ["Visualização corporal com face swap", "Vídeo com morphing de transformação", "Premium: Cronologia de 8 semanas", "Análise científica de gordura corporal"]
    }
  },
  {
    id: 'challenge',
    icon: Trophy,
    color: '#f59e0b',
    gradient: 'from-amber-400 to-orange-500',
    path: '/challenge-8k',
    title: {
      en: '8K Challenge',
      pt: 'Desafio 8K'
    },
    subtitle: {
      en: '8 weeks to transform',
      pt: '8 semanas para transformar'
    },
    content: {
      en: "The 8K Challenge is an 8-week structured program that combines everything: nutrition, fasting, training, and mindset. I guide you week by week, adapt the plan to your progress, and celebrate every milestone with you.",
      pt: "O Desafio 8K é um programa estruturado de 8 semanas que combina tudo: nutrição, jejum, treino e mentalidade. Guio-te semana a semana, adapto o plano ao teu progresso, e celebro cada marco contigo."
    },
    tips: {
      en: ["8-week transformation program", "Weekly milestones", "Personalized adaptation", "Certificate on completion"],
      pt: ["Programa de 8 semanas", "Marcos semanais", "Adaptação personalizada", "Certificado ao completar"]
    }
  },
  {
    id: 'coach',
    icon: Brain,
    color: '#10b981',
    gradient: 'from-emerald-400 to-teal-500',
    path: '/keto-buddy',
    title: {
      en: 'AI Coach',
      pt: 'Coach IA'
    },
    subtitle: {
      en: 'Your 24/7 transformation partner',
      pt: 'O teu parceiro de transformação 24/7'
    },
    content: {
      en: "This is where we talk. Ask me anything about keto, nutrition, training, motivation - I'm here for you. I remember our conversations and learn from them to give you better advice over time.",
      pt: "É aqui que conversamos. Pergunta-me qualquer coisa sobre keto, nutrição, treino, motivação - estou aqui para ti. Lembro-me das nossas conversas e aprendo com elas para te dar melhores conselhos ao longo do tempo."
    },
    tips: {
      en: ["24/7 availability", "Personalized advice", "Learns from you", "Motivation & support"],
      pt: ["Disponível 24/7", "Conselhos personalizados", "Aprende contigo", "Motivação e suporte"]
    }
  },
  {
    id: 'community',
    icon: Users,
    color: '#6366f1',
    gradient: 'from-indigo-400 to-indigo-600',
    path: '/community',
    title: {
      en: 'Community',
      pt: 'Comunidade'
    },
    subtitle: {
      en: 'You are not alone',
      pt: 'Não estás sozinho'
    },
    content: {
      en: "Join thousands of people on the same journey. Share your progress, get inspired by others, ask questions, and celebrate victories together. Transformation is better when shared.",
      pt: "Junta-te a milhares de pessoas na mesma jornada. Partilha o teu progresso, inspira-te com outros, faz perguntas, e celebra vitórias juntos. A transformação é melhor quando partilhada."
    },
    tips: {
      en: ["5K+ active members", "Share progress", "Get support", "Find motivation"],
      pt: ["5K+ membros ativos", "Partilha progresso", "Obtém suporte", "Encontra motivação"]
    }
  },
  {
    id: 'learn',
    icon: GraduationCap,
    color: '#3b82f6',
    gradient: 'from-blue-400 to-blue-600',
    path: '/content',
    title: {
      en: 'Learn',
      pt: 'Aprender'
    },
    subtitle: {
      en: 'Knowledge is power',
      pt: 'Conhecimento é poder'
    },
    content: {
      en: "Access our complete education library. Learn the science behind keto, understand your body, master fasting protocols, and become an expert in your own transformation. Knowledge gives you control.",
      pt: "Acede à nossa biblioteca educativa completa. Aprende a ciência por trás do keto, entende o teu corpo, domina protocolos de jejum, e torna-te um especialista na tua própria transformação. O conhecimento dá-te controlo."
    },
    tips: {
      en: ["Video tutorials", "Science articles", "Expert guides", "Progress modules"],
      pt: ["Tutoriais em vídeo", "Artigos científicos", "Guias de especialistas", "Módulos de progresso"]
    }
  },
  {
    id: 'finish',
    icon: Rocket,
    color: '#a3e635',
    gradient: 'from-[#a3e635] to-emerald-500',
    title: {
      en: "You're Ready!",
      pt: 'Estás Pronto!'
    },
    subtitle: {
      en: 'Your transformation begins now',
      pt: 'A tua transformação começa agora'
    },
    content: {
      en: "That's everything! Remember, I'm always here - just look for me in any section of the app. Your transformation journey starts now. Let's do this together!",
      pt: "É tudo! Lembra-te, estou sempre aqui - procura-me em qualquer secção da app. A tua jornada de transformação começa agora. Vamos fazer isto juntos!"
    },
    tips: {
      en: ["Start with the 8K Challenge", "Or explore freely", "I'm always one tap away"],
      pt: ["Começa com o Desafio 8K", "Ou explora livremente", "Estou sempre a um toque de distância"]
    }
  }
];

// Keto Buddy Spirit for Tour
// ============================================
// 🌟 ETHEREAL SPIRIT - SOFT & TRANSPARENT
// A gentle, floating intelligence that blends into the environment
// Soft, delicate, like morning mist with green luminescence
// ============================================
const TourSpirit = ({ isTyping = false }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const breathRef = useRef(0);
  const timeRef = useRef(0);
  
  // Softer, more delicate green palette
  const SOFT_GREEN = 'rgba(163, 230, 53, 0.7)'; // #a3e635 but softer
  const LIGHT_GREEN = 'rgba(190, 242, 100, 0.5)';
  const GLOW_GREEN = 'rgba(163, 230, 53, 0.2)';
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width = 180;
    const height = canvas.height = 180;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // Fewer particles for softer look
    const PARTICLE_COUNT = 55;
    particlesRef.current = [];
    
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 40 + 15;
      particlesRef.current.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        baseX: centerX,
        baseY: centerY,
        angle: angle,
        radius: radius,
        size: Math.random() * 2 + 0.8,
        opacity: Math.random() * 0.4 + 0.15, // Lower opacity
        orbitSpeed: (Math.random() - 0.5) * 0.015,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: Math.random() * 0.03 + 0.01,
        trail: [],
        maxTrail: Math.floor(Math.random() * 4) + 2,
        layer: Math.random()
      });
    }
    
    const animate = () => {
      timeRef.current += 0.016;
      
      // Gentle breathing
      const breathSpeed = isTyping ? 0.08 : 0.02;
      breathRef.current += breathSpeed;
      const breathScale = 1 + Math.sin(breathRef.current) * (isTyping ? 0.1 : 0.05);
      
      // Clear canvas FULLY for transparency - no black background!
      ctx.clearRect(0, 0, width, height);
      
      // Soft central glow - very subtle
      const coreGradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, 50 * breathScale
      );
      coreGradient.addColorStop(0, 'rgba(163, 230, 53, 0.12)');
      coreGradient.addColorStop(0.5, 'rgba(163, 230, 53, 0.05)');
      coreGradient.addColorStop(1, 'rgba(163, 230, 53, 0)');
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 50 * breathScale, 0, Math.PI * 2);
      ctx.fill();
      
      // Sort and draw particles
      const sortedParticles = [...particlesRef.current].sort((a, b) => a.layer - b.layer);
      
      sortedParticles.forEach((p, i) => {
        // Gentle organic movement
        p.wobble += p.wobbleSpeed * (isTyping ? 1.5 : 1);
        p.angle += p.orbitSpeed * (isTyping ? 1.3 : 1);
        
        const wobbleOffset = Math.sin(p.wobble) * 5;
        const pulseOffset = isTyping ? Math.sin(timeRef.current * 6 + i) * 3 : 0;
        const targetRadius = (p.radius + wobbleOffset + pulseOffset) * breathScale;
        
        const targetX = centerX + Math.cos(p.angle) * targetRadius;
        const targetY = centerY + Math.sin(p.angle) * targetRadius;
        
        p.x += (targetX - p.x) * 0.08;
        p.y += (targetY - p.y) * 0.08;
        
        // Softer trails
        p.trail.unshift({ x: p.x, y: p.y, opacity: p.opacity });
        if (p.trail.length > p.maxTrail) p.trail.pop();
        
        p.trail.forEach((point, idx) => {
          const trailOpacity = (1 - idx / p.maxTrail) * point.opacity * 0.3;
          const trailSize = p.size * (1 - idx / p.maxTrail) * 0.6;
          
          ctx.beginPath();
          ctx.arc(point.x, point.y, trailSize, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(163, 230, 53, ${trailOpacity})`;
          ctx.fill();
        });
        
        // Soft particle glow
        const glowSize = p.size * 4;
        const particleGlow = ctx.createRadialGradient(
          p.x, p.y, 0,
          p.x, p.y, glowSize
        );
        
        const intensity = 0.3 + p.layer * 0.4;
        particleGlow.addColorStop(0, `rgba(190, 242, 100, ${p.opacity * intensity})`);
        particleGlow.addColorStop(0.4, `rgba(163, 230, 53, ${p.opacity * 0.3 * intensity})`);
        particleGlow.addColorStop(1, 'rgba(163, 230, 53, 0)');
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowSize, 0, Math.PI * 2);
        ctx.fillStyle = particleGlow;
        ctx.fill();
        
        // Soft white-green core
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220, 255, 220, ${p.opacity * intensity * 0.8})`;
        ctx.fill();
      });
      
      // Subtle pulse when typing
      if (isTyping) {
        const pulseIntensity = (Math.sin(timeRef.current * 5) + 1) / 2;
        const pulseGradient = ctx.createRadialGradient(
          centerX, centerY, 0,
          centerX, centerY, 25
        );
        pulseGradient.addColorStop(0, `rgba(163, 230, 53, ${0.15 * pulseIntensity})`);
        pulseGradient.addColorStop(1, 'rgba(163, 230, 53, 0)');
        
        ctx.fillStyle = pulseGradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, 25, 0, Math.PI * 2);
        ctx.fill();
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isTyping]);
  
  return (
    <div className="relative w-44 h-44 mx-auto mb-4">
      {/* Very soft ambient glow */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(163, 230, 53, 0.08) 0%, rgba(163, 230, 53, 0.03) 50%, transparent 70%)',
          filter: 'blur(15px)'
        }}
        animate={{
          scale: isTyping ? [1, 1.15, 1] : [1, 1.08, 1],
          opacity: isTyping ? [0.5, 0.8, 0.5] : [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: isTyping ? 0.8 : 2.5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Transparent canvas - no black background */}
      <canvas
        ref={canvasRef}
        className="relative z-10 w-full h-full"
        style={{ 
          filter: 'blur(0.3px)',
        }}
      />
      
      {/* Fewer, softer floating sparks */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: 'rgba(190, 242, 100, 0.6)',
            boxShadow: '0 0 6px rgba(163, 230, 53, 0.4)',
            left: '50%',
            top: '50%'
          }}
          animate={{
            x: [0, Math.cos((i * 72) * Math.PI / 180) * 65, 0],
            y: [0, Math.sin((i * 72) * Math.PI / 180) * 65, 0],
            opacity: [0, 0.5, 0],
            scale: [0.3, 0.8, 0.3]
          }}
          transition={{
            duration: 4 + i * 0.5,
            delay: i * 0.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Typewriter effect hook - OPTIMIZED FOR PERFORMANCE
const useTypewriter = (text, speed = 8, startTyping = true) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!startTyping) {
      setDisplayText('');
      setIsComplete(false);
      return;
    }

    setDisplayText('');
    setIsComplete(false);
    let index = 0;
    
    // Use requestAnimationFrame for smoother performance
    let lastTime = 0;
    let animationId;
    
    const animate = (currentTime) => {
      if (currentTime - lastTime >= speed) {
        if (index < text.length) {
          // Type multiple characters at once for faster display
          const charsToAdd = Math.min(2, text.length - index);
          setDisplayText(text.slice(0, index + charsToAdd));
          index += charsToAdd;
          lastTime = currentTime;
        } else {
          setIsComplete(true);
          return;
        }
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationId);
  }, [text, speed, startTyping]);

  return { displayText, isComplete };
};

// Main Tour Component
export default function KetoBuddyTour({ 
  isOpen, 
  onClose, 
  onComplete,
  startModule = 0 
}) {
  const { t } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';
  const [currentModule, setCurrentModule] = useState(startModule);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  
  const module = TOUR_MODULES[currentModule];
  const progress = ((currentModule + 1) / TOUR_MODULES.length) * 100;
  
  const { displayText, isComplete } = useTypewriter(
    module?.content?.[lang] || '',
    25,
    true
  );

  // Update typing state
  useEffect(() => {
    setIsTyping(!isComplete);
  }, [isComplete]);

  // Reset when module changes
  useEffect(() => {
    setIsTyping(true);
  }, [currentModule]);

  // Browser TTS
  const speak = useCallback((text) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang === 'pt' ? 'pt-PT' : lang === 'es' ? 'es-ES' : 'en-US';
    utterance.rate = 0.9;
    window.speechSynthesis.speak(utterance);
  }, [voiceEnabled, lang]);

  // Speak when module changes
  useEffect(() => {
    if (voiceEnabled && module) {
      speak(module.content[lang]);
    }
  }, [currentModule, voiceEnabled]);

  // Navigation
  const nextModule = () => {
    if (currentModule < TOUR_MODULES.length - 1) {
      setCurrentModule(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const prevModule = () => {
    if (currentModule > 0) {
      setCurrentModule(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    window.speechSynthesis?.cancel();
    onComplete?.();
    onClose?.();
  };

  const skipToEnd = () => {
    setDisplayText(module.content[lang]);
    setIsTyping(false);
  };

  if (!isOpen || !module) return null;

  const Icon = module.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 pb-20 sm:pb-24 bg-black/90 backdrop-blur-sm"
      >
        <motion.div
          data-testid="keto-buddy-tour-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-lg bg-gradient-to-b from-zinc-900 to-black rounded-3xl border border-zinc-800 overflow-hidden"
        >
          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-zinc-800">
            <motion.div 
              className="h-full bg-gradient-to-r from-[#a3e635] to-emerald-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Close button */}
          <button
            data-testid="keto-buddy-tour-close"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800/50 hover:bg-zinc-700 transition-colors z-10"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>

          {/* Voice toggle */}
          <button
            onClick={() => {
              setVoiceEnabled(!voiceEnabled);
              if (!voiceEnabled) {
                speak(module.content[lang]);
              } else {
                window.speechSynthesis?.cancel();
              }
            }}
            className="absolute top-4 left-4 p-2 rounded-full bg-zinc-800/50 hover:bg-zinc-700 transition-colors z-10"
          >
            {voiceEnabled ? (
              <Volume2 className="w-5 h-5 text-[#a3e635]" />
            ) : (
              <VolumeX className="w-5 h-5 text-zinc-400" />
            )}
          </button>

          {/* Content */}
          <div className="pt-16 pb-6 px-6">
            {/* Module counter */}
            <div className="text-center mb-4">
              <span className="text-xs text-zinc-500">
                {currentModule + 1} / {TOUR_MODULES.length}
              </span>
            </div>

            {/* Spirit */}
            <TourSpirit isTyping={isTyping} />

            {/* Icon badge */}
            <motion.div 
              className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${module.gradient} flex items-center justify-center shadow-lg`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.05, duration: 0.3 }}
            >
              <Icon className="w-7 h-7 text-black" />
            </motion.div>

            {/* Title */}
            <motion.h2 
              className="text-2xl font-bold text-white text-center mb-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.2 }}
            >
              {module.title[lang]}
            </motion.h2>

            {/* Subtitle */}
            <motion.p 
              className="text-sm text-zinc-400 text-center mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.12, duration: 0.2 }}
            >
              {module.subtitle[lang]}
            </motion.p>

            {/* Content with typewriter */}
            <motion.div 
              className="min-h-[100px] mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.2 }}
            >
              <p className="text-zinc-300 text-center leading-relaxed">
                {displayText}
                {isTyping && (
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    className="inline-block w-0.5 h-5 bg-[#a3e635] ml-1 align-middle"
                  />
                )}
              </p>
              
              {/* Skip typing button */}
              {isTyping && (
                <button
                  onClick={skipToEnd}
                  className="block mx-auto mt-3 text-xs text-zinc-500 hover:text-zinc-400"
                >
                  {t('tour.showAll')}
                </button>
              )}
            </motion.div>

            {/* Tips */}
            {isComplete && module.tips && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                className="mb-6 p-4 bg-zinc-800/50 rounded-xl"
              >
                <div className="flex flex-wrap gap-2">
                  {(module.tips[lang] || module.tips['en'] || module.tips['pt'] || []).map((tip, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.03, duration: 0.1 }}
                      className="text-xs px-3 py-1.5 bg-zinc-700/50 rounded-full text-zinc-300"
                    >
                      ✓ {tip}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={prevModule}
                disabled={currentModule === 0}
                className="flex-1 border-zinc-700 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('tour.previous')}
              </Button>
              
              <Button
                onClick={nextModule}
                className={`flex-1 bg-gradient-to-r ${module.gradient} text-black font-bold hover:opacity-90`}
              >
                {currentModule === TOUR_MODULES.length - 1 ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {t('tour.start')}
                  </>
                ) : (
                  <>
                    {t('tour.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>

            {/* Skip tour */}
            <button
              onClick={handleComplete}
              className="block mx-auto mt-4 text-xs text-zinc-500 hover:text-zinc-400"
            >
              {t('tour.skipTour')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Export tour modules for use elsewhere
export { TOUR_MODULES };
